import { Button } from "@/components/flexkit/Button";
import { EnvironmentClassSelect } from "@/components/EnvironmentClassSelect";
import { type EnvironmentClassEntry, useEnvironmentClassEntries } from "@/hooks/use-environment-class-entries";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useCreateEnvironment } from "@/queries/environment-queries";
import { getDetailsURL } from "@/routes/environments/details-url";
import { formatError } from "@/utils/errors";
import { RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { type FC, type FormEvent, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EnvironmentCreateError } from "../components/EnvironmentCreateError";

export const BlankTab: FC<{
    onClose: () => void;
}> = ({ onClose }) => {
    const navigate = useNavigate();
    const { toast } = useToast();
    const [createError, setCreateError] = useState<Error | null>(null);
    const [selectedClassEntry, setSelectedClassEntry] = useState<EnvironmentClassEntry>();

    const { environmentClassEntries, isLoading: isLoadingEnvClasses } = useEnvironmentClassEntries();

    // select the first enabled environment class if none is selected
    useEffect(() => {
        if (!isLoadingEnvClasses && !selectedClassEntry && environmentClassEntries.length > 0) {
            const firstActive = environmentClassEntries.find(
                (et) => !et.policyRestricted && et.runner?.status?.phase === RunnerPhase.ACTIVE,
            );
            if (firstActive) {
                setSelectedClassEntry(firstActive);
            }
        }
    }, [isLoadingEnvClasses, selectedClassEntry, environmentClassEntries]);

    const createEnvironment = useCreateEnvironment();

    const handleContinue = useCallback(async () => {
        try {
            setCreateError(null);
            const environment = await createEnvironment.mutateAsync({
                type: "blank",
                classID: selectedClassEntry?.clazz?.id ?? "",
            });
            onClose();
            navigate(getDetailsURL(environment));
        } catch (error) {
            setCreateError(error instanceof Error ? error : new Error(String(error)));
            toast({ title: "Failed to create an environment", description: formatError(error) });
        }
    }, [createEnvironment, selectedClassEntry?.clazz?.id, onClose, navigate, toast]);

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            const runnerID = selectedClassEntry?.runner?.runnerId;
            const classID = selectedClassEntry?.clazz?.id;
            if (!runnerID || !classID) {
                console.log(`Invalid runnerId=${runnerID} or classID=${classID}`);
                return;
            }

            await handleContinue();
        },
        [handleContinue, selectedClassEntry],
    );

    const createButtonDisabled = selectedClassEntry === undefined || !!createError;

    return (
        <form onSubmit={handleSubmit} className="pt-8">
            <div className="flex flex-col gap-2" id="create-environment-form">
                <EnvironmentClassSelect
                    label="Environment class"
                    value={selectedClassEntry}
                    onChange={(v) => setSelectedClassEntry(v)}
                    name="environment-class"
                    loading={isLoadingEnvClasses}
                    disabled={false}
                    environmentClassEntries={environmentClassEntries}
                />

                <div data-testid="errors" className="flex min-h-14 flex-col items-center justify-center gap-2 text-sm">
                    {createError && <EnvironmentCreateError error={createError} />}
                </div>

                <div className="flex flex-col gap-2">
                    <Button
                        type="submit"
                        variant="primary"
                        className="w-full text-base font-bold"
                        loading={createEnvironment.isPending}
                        disabled={createButtonDisabled}
                        data-testid="create-environment-button"
                    >
                        Create
                    </Button>
                </div>
            </div>
        </form>
    );
};
